import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import {
  Clock20Px,
  formatTimeInTimeZone,
  mainColours,
  PersonCircle20Px,
  TempAmbient20Px,
  TempChilled20Px,
  TempFrozen20Px,
  Workstation20Px
} from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { tempZoneBasedColor } from "~/lib/tempZone";
import { TemperatureZone } from "~/redux/public/inventory.openApi";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { PickDto, ToteSummaryDto } from "~/types/api";

export interface ToteCardProps {
  tote: ToteSummaryDto;
  picks: PickDto[];
  totalTotes: number;
}

const getTemperatureZoneIcon = (tempZone: string | null) => {
  if (!tempZone) return null;

  const zone = tempZone.toLowerCase();
  const temperatureZoneIconStyle = {
    height: 20,
    width: 20,
    fill: mainColours.icon.secondary
  };
  if (zone === "ambient")
    return <TempAmbient20Px style={temperatureZoneIconStyle} />;
  if (zone === "chilled")
    return <TempChilled20Px style={temperatureZoneIconStyle} />;
  if (zone === "frozen")
    return <TempFrozen20Px style={temperatureZoneIconStyle} />;

  return null;
};

export function ToteCard({ tote, picks, totalTotes }: ToteCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const usersFC = useAppSelector(selectUsersFulfillmentCenter);
  const { data: workstations } = useGetWorkstationsQuery();

  const tempZone = tote.temperatureZone?.toLowerCase() as TemperatureZone;

  let chipColor = "background.gray";
  switch (tote.status.toLowerCase()) {
    case "picked":
      chipColor = mainColours.label[9];
      break;
    case "picking":
      chipColor = mainColours.label[12];
      break;
  }
  const completedPicks = picks.filter(
    (pick) =>
      pick.status.toLowerCase() === "completed" || pick.completedTimestamp
  );
  const workstationIds = [
    ...new Set(completedPicks.map((p) => p.workstationId))
  ];
  const workstationPicked = workstationIds.length
    ? workstationIds
        .map((id) => {
          const ws = workstations?.find((w) => w.id === id);
          return ws?.deviceId ?? id;
        })
        .join(", ")
    : null;

  const pickedByNames = [
    ...new Set(
      completedPicks.map((p) => extractUserNameFromAuth0Id(p.completedBy))
    )
  ];
  const pickedBy = pickedByNames.length > 0 ? pickedByNames.join(", ") : null;

  const completed = tote.pickedTimestamp
    ? formatTimeInTimeZone(tote.pickedTimestamp, usersFC?.timeZone)
    : null;

  return (
    <Box
      border={`1px solid ${theme.palette.divider}`}
      borderRadius={1}
      marginBottom={2}
      position="relative"
      overflow="hidden"
      role="region"
    >
      <Stack direction="row">
        <Box
          width={24}
          sx={{
            borderRadius: "8px 0 0 8px",
            backgroundColor: tempZoneBasedColor(tempZone || ""),
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end"
          }}
        >
          <Box paddingLeft={"1px"}>{getTemperatureZoneIcon(tempZone)}</Box>
        </Box>
        <Stack direction="column" gap={1} width="100%" padding={2} minWidth={0}>
          <Stack direction="column" gap={0} paddingBottom={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                {t("tote id")}: {tote.externalToteId}
              </Typography>
              <Stack direction="row" gap={1} alignItems="center">
                <Chip
                  label={tote.status}
                  size="small"
                  sx={{ backgroundColor: chipColor }}
                />
                <Typography variant="subtitle1">
                  {tote.toteNumber}/{totalTotes} {t("totes")}
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="textSecondary">
              {t("batch names")}: {tote.batchNames.join(", ")}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} justifyContent="space-between">
            <Stack
              direction="column"
              gap={1}
              paddingRight={2}
              sx={{ flex: "2 1 55%", minWidth: 0 }}
            >
              {picks.map((pick) => {
                return (
                  <Stack direction="column" gap={0} key={pick.pickId}>
                    <Stack
                      direction="row"
                      gap={2}
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle2" noWrap>
                        {pick.productName}
                      </Typography>
                      <Typography variant="body2" minWidth="60px">
                        {t("qty")}: {pick.quantity.value} {pick.quantity.units}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" paddingLeft={1}>
                      {t("upc")}: {pick.upc}
                    </Typography>
                    <Typography variant="body2" paddingLeft={1}>
                      {t("sku")}: {pick.sku}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
            <Stack
              direction="column"
              paddingLeft={3}
              sx={{
                flex: "1 1 45%",
                minWidth: 0,
                borderLeft: `1px solid ${theme.palette.divider}`
              }}
              justifyContent="center"
              alignItems="start"
            >
              <Stack direction="row" gap={1}>
                <Workstation20Px
                  style={{
                    fill: theme.palette.text.secondary,
                    width: "14px"
                  }}
                />
                <Typography
                  variant="body2"
                  color={workstationPicked ? "textPrimary" : "textSecondary"}
                  role="status"
                  aria-label="workstation picked"
                >
                  {t("workstation picked")}: {workstationPicked ?? "-"}
                </Typography>
              </Stack>
              <Stack direction="row" gap={1}>
                <PersonCircle20Px
                  style={{
                    fill: theme.palette.text.secondary,
                    width: "14px"
                  }}
                />
                <Typography
                  variant="body2"
                  color={pickedBy ? "textPrimary" : "textSecondary"}
                  role="status"
                  aria-label="picked by"
                >
                  {t("picked by")}: {pickedBy ?? "-"}
                </Typography>
              </Stack>
              <Stack direction="row" gap={1}>
                <Clock20Px
                  style={{
                    fill: theme.palette.text.secondary,
                    width: "14px"
                  }}
                />
                <Typography
                  variant="body2"
                  color={completed ? "textPrimary" : "textSecondary"}
                >
                  {t("completed")}: {completed ?? "-"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
